// =====================================================================
// USAF PFRA Scoring (effective 1 Mar 26)
// Source: https://www.afpc.af.mil/Portals/70/documents/FITNESS/PFRA Scoring Charts.pdf
// =====================================================================
// Age bands index: 0=Under 25, 1=25-29, 2=30-34, 3=35-39, 4=40-44, 5=45-49, 6=50-54, 7=55-59, 8=60+
const AGE_BANDS = ["<25", "25-29", "30-34", "35-39", "40-44", "45-49", "50-54", "55-59", "60+"];
const SCORING_RULE_VERSION = "AFPC-PFRA-2026-03-17";
const AGE_BAND_REPRESENTATIVE = {
  "<25": 24,
  "25-29": 27,
  "30-34": 32,
  "35-39": 37,
  "40-44": 42,
  "45-49": 47,
  "50-54": 52,
  "55-59": 57,
  "60+": 60
};
const ageBandIdx = (age) => {
  const a = Number(age);
  if (!a || a < 25) return 0;
  if (a >= 60) return 8;
  return Math.min(8, Math.floor((a - 25) / 5) + 1);
};

// BCA: Waist-to-Height ratio. Same for M/F, all ages. 0-20 points.
// Higher ratio = lower points. Min passing = 17 pts (≤0.52).
const scoreBCA = (whtr) => {
  const r = Number(whtr);
  if (!r) return null;
  if (r <= 0.49) return 20.0;
  if (r >= 0.60) return 0;
  // Each 0.01 above 0.49 = ~1.5 pt drop, but follows a specific table
  for (const [t, p] of BCA_TBL) if (r <= t + 0.001) return p;
  return 0;
};

const BCA_TBL = [
  [0.49, 20], [0.50, 19], [0.51, 18], [0.52, 17], [0.53, 16], [0.54, 15],
  [0.55, 12.5], [0.56, 10], [0.57, 7.5], [0.58, 5], [0.59, 2.5],
];

// Helper: lookup score in a "thresholds descending" table.
// Each row = [threshold, points]. Highest threshold met (>= or <=) wins.
const lookupHigher = (rows, val, ageIdx, isFemale) => {
  // rows: array of {pts, mUnder25, fUnder25, m25_29, f25_29, ...} flattened to [pts, ...18 cols]
  if (val == null || val === "" || isNaN(Number(val))) return null;
  const v = Number(val);
  const col = ageIdx * 2 + (isFemale ? 1 : 0) + 1; // +1 because col 0 is points
  for (const row of rows) {
    if (v >= row[col]) return row[0];
  }
  return 0;
};
const lookupLower = (rows, val, ageIdx, isFemale) => {
  // For run/HAMR/plank time where lower is better (run/walk) or specific direction.
  if (val == null || val === "") return null;
  const v = Number(val);
  const col = ageIdx * 2 + (isFemale ? 1 : 0) + 1;
  for (const row of rows) {
    if (v <= row[col]) return row[0];
  }
  return 0;
};

// Convert "mm:ss" to seconds
const mmssToSec = (s) => {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d+):(\d{1,2})$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

// PUSH-UPS (15 pt max). Columns: pts, M<25, F<25, M25-29, F25-29, ... M60+, F60+
const PUSHUP_TBL = [
  [15.0, 67, 50, 63, 47, 60, 44, 56, 42, 52, 39, 49, 36, 45, 34, 42, 31, 38, 28],
  [14.5, 66, 49, 62, 46, 59, 42, 55, 41, 51, 38, 47, 35, 44, 33, 41, 30, 37, 27],
  [14.0, 64, 47, 60, 44, 57, 41, 53, 40, 50, 37, 46, 34, 43, 32, 40, 29, 36, 26],
  [13.5, 63, 46, 59, 43, 56, 40, 52, 38, 49, 36, 45, 33, 42, 31, 39, 28, 35, 25],
  [13.0, 61, 44, 57, 42, 55, 39, 51, 37, 48, 35, 44, 32, 41, 30, 38, 27, 34, 24],
  [12.5, 60, 43, 56, 40, 53, 37, 49, 36, 46, 33, 43, 30, 39, 29, 36, 26, 33, 23],
  [12.0, 58, 42, 55, 39, 52, 36, 48, 35, 45, 32, 42, 29, 38, 28, 35, 25, 32, 22],
  [11.5, 57, 40, 53, 38, 51, 35, 47, 33, 44, 31, 40, 28, 37, 26, 34, 24, 31, 21],
  [11.0, 55, 39, 52, 36, 49, 34, 45, 32, 42, 30, 39, 27, 36, 25, 33, 23, 30, 20],
  [10.5, 54, 37, 50, 35, 48, 32, 44, 31, 41, 29, 38, 26, 35, 24, 32, 22, 29, 19],
  [10.0, 52, 36, 49, 34, 47, 31, 43, 30, 40, 27, 37, 25, 34, 23, 31, 21, 28, 18],
  [9.5, 51, 35, 48, 32, 45, 30, 41, 28, 39, 26, 36, 24, 33, 22, 30, 20, 27, 17],
  [9.0, 49, 33, 46, 31, 44, 29, 40, 27, 37, 25, 35, 23, 32, 21, 29, 19, 26, 16],
  [8.5, 48, 32, 45, 30, 43, 27, 39, 26, 36, 24, 33, 21, 30, 20, 27, 17, 24, 15],
  [8.0, 46, 30, 43, 29, 41, 26, 38, 25, 35, 23, 32, 20, 29, 19, 26, 16, 23, 14],
  [7.5, 45, 29, 42, 27, 40, 25, 36, 23, 33, 21, 31, 19, 28, 18, 25, 15, 22, 13],
  [7.0, 43, 28, 41, 26, 39, 24, 35, 22, 32, 20, 30, 18, 27, 17, 24, 14, 21, 12],
  [6.5, 42, 26, 39, 25, 37, 22, 34, 21, 31, 19, 29, 17, 26, 16, 23, 13, 20, 11],
  [6.0, 40, 25, 38, 23, 36, 21, 32, 20, 30, 18, 28, 16, 25, 15, 22, 12, 19, 10],
  [5.5, 39, 23, 36, 22, 35, 20, 31, 18, 28, 17, 26, 15, 24, 13, 21, 11, 18, 9],
  [5.0, 37, 22, 35, 21, 33, 19, 30, 17, 27, 15, 25, 14, 23, 12, 20, 10, 17, 8],
  [4.5, 36, 21, 34, 19, 32, 17, 28, 16, 26, 14, 24, 12, 21, 11, 18, 9, 16, 7],
  [4.0, 34, 19, 32, 18, 31, 16, 27, 15, 24, 13, 23, 11, 20, 10, 17, 8, 15, 6],
  [3.5, 33, 18, 31, 17, 29, 15, 26, 13, 23, 12, 22, 10, 19, 9, 16, 7, 14, 5],
  [3.0, 31, 16, 29, 15, 26, 14, 24, 12, 22, 11, 21, 9, 18, 8, 15, 6, 13, 4],
  [2.5, 30, 15, 28, 14, 26, 12, 23, 11, 21, 10, 19, 8, 17, 7, 14, 5, 12, 3],
];

const HR_PUSHUP_TBL = [
  [15.0, 52, 42, 50, 40, 48, 38, 46, 36, 44, 34, 42, 32, 40, 30, 38, 28, 36, 26],
  [14.5, 51, 41, 49, 39, 47, 37, 45, 35, 43, 33, 41, 31, 39, 29, 37, 27, 35, 25],
  [14.0, 50, 40, 48, 38, 46, 36, 44, 34, 42, 32, 40, 30, 38, 28, 36, 26, 34, 24],
  [13.5, 49, 39, 47, 37, 45, 35, 43, 33, 41, 31, 39, 29, 37, 27, 35, 25, 33, 23],
  [13.0, 48, 38, 46, 36, 44, 34, 42, 32, 40, 30, 38, 28, 36, 26, 34, 24, 32, 22],
  [12.5, 47, 37, 45, 35, 43, 33, 41, 31, 39, 29, 37, 27, 35, 25, 33, 23, 31, 21],
  [12.0, 46, 36, 44, 34, 42, 32, 40, 30, 38, 28, 36, 26, 34, 24, 32, 22, 30, 20],
  [11.5, 45, 35, 43, 33, 41, 31, 39, 29, 37, 27, 35, 25, 33, 23, 31, 21, 29, 19],
  [11.0, 44, 34, 42, 32, 40, 30, 38, 28, 36, 26, 34, 24, 32, 22, 30, 20, 28, 18],
  [10.5, 43, 33, 41, 31, 39, 29, 37, 27, 35, 25, 33, 23, 31, 21, 29, 19, 27, 17],
  [10.0, 42, 32, 40, 30, 38, 28, 36, 26, 34, 24, 32, 22, 30, 20, 28, 18, 26, 16],
  [9.5, 41, 31, 39, 29, 37, 27, 35, 25, 33, 23, 31, 21, 29, 19, 27, 17, 25, 15],
  [9.0, 40, 30, 38, 28, 36, 26, 34, 24, 32, 22, 30, 20, 28, 18, 26, 16, 24, 14],
  [8.5, 39, 29, 37, 27, 35, 25, 33, 23, 31, 21, 29, 19, 27, 17, 25, 15, 23, 13],
  [8.0, 38, 28, 36, 26, 34, 24, 32, 22, 30, 20, 28, 18, 26, 16, 24, 14, 22, 12],
  [7.5, 37, 27, 35, 25, 33, 23, 31, 21, 29, 19, 27, 17, 25, 15, 23, 13, 21, 11],
  [7.0, 36, 26, 34, 24, 32, 22, 30, 20, 28, 18, 26, 16, 24, 14, 22, 12, 20, 10],
  [6.5, 35, 25, 33, 23, 31, 21, 29, 19, 27, 17, 25, 15, 23, 13, 21, 11, 19, 9],
  [6.0, 34, 24, 32, 22, 30, 20, 28, 18, 26, 16, 24, 14, 22, 12, 20, 10, 18, 8],
  [5.5, 33, 23, 31, 21, 29, 19, 27, 17, 25, 15, 23, 13, 21, 11, 19, 9, 17, 7],
  [5.0, 32, 22, 30, 20, 28, 18, 26, 16, 24, 14, 22, 12, 20, 10, 18, 8, 16, 6],
  [4.5, 31, 21, 29, 19, 27, 17, 25, 15, 23, 13, 21, 11, 19, 9, 17, 7, 15, 5],
  [4.0, 30, 20, 28, 18, 26, 16, 24, 14, 22, 12, 20, 10, 18, 8, 16, 6, 14, 4],
  [3.5, 29, 19, 27, 17, 25, 15, 23, 13, 21, 11, 19, 9, 17, 7, 15, 5, 13, 3],
  [3.0, 28, 18, 26, 16, 24, 14, 22, 12, 20, 10, 18, 8, 16, 6, 14, 4, 12, 2],
  [2.5, 27, 17, 25, 15, 23, 13, 21, 11, 19, 9, 17, 7, 15, 5, 13, 3, 11, 1],
];

const SITUP_TBL = [
  [15.0, 58, 54, 56, 50, 54, 45, 52, 43, 50, 41, 48, 35, 46, 34, 44, 32, 42, 31],
  [14.5, 57, 53, 55, 49, 53, 44, 51, 42, 49, 40, 47, 34, 45, 33, 43, 31, 41, 30],
  [14.0, 56, 52, 54, 48, 52, 43, 50, 41, 48, 39, 46, 33, 44, 32, 42, 30, 40, 29],
  [13.5, 55, 51, 53, 47, 51, 42, 49, 40, 47, 38, 45, 32, 43, 31, 41, 29, 39, 28],
  [13.0, 54, 50, 52, 46, 50, 41, 48, 39, 46, 37, 44, 31, 42, 30, 40, 28, 38, 27],
  [12.5, 53, 49, 51, 45, 49, 40, 47, 38, 45, 36, 43, 30, 41, 29, 39, 27, 37, 26],
  [12.0, 52, 48, 50, 44, 48, 39, 46, 37, 44, 35, 42, 29, 40, 28, 38, 26, 36, 25],
  [11.5, 51, 47, 49, 43, 47, 38, 45, 36, 43, 34, 41, 28, 39, 27, 37, 25, 35, 24],
  [11.0, 50, 46, 48, 42, 46, 37, 44, 35, 42, 33, 40, 27, 38, 26, 36, 24, 34, 23],
  [10.5, 49, 45, 47, 41, 45, 36, 43, 34, 41, 32, 39, 26, 37, 25, 35, 23, 33, 22],
  [10.0, 48, 44, 46, 40, 44, 35, 42, 33, 40, 31, 38, 25, 36, 24, 34, 22, 32, 21],
  [9.5, 47, 43, 45, 39, 43, 34, 41, 32, 39, 30, 37, 24, 35, 23, 33, 21, 31, 20],
  [9.0, 46, 42, 44, 38, 42, 33, 40, 31, 38, 29, 36, 23, 34, 22, 32, 20, 30, 19],
  [8.5, 45, 41, 43, 37, 41, 32, 39, 30, 37, 28, 35, 22, 33, 21, 31, 19, 29, 18],
  [8.0, 44, 40, 42, 36, 40, 31, 38, 29, 36, 27, 34, 21, 32, 20, 30, 18, 28, 17],
  [7.5, 43, 39, 41, 35, 39, 30, 37, 28, 35, 26, 33, 20, 31, 19, 29, 17, 27, 16],
  [7.0, 42, 38, 40, 34, 38, 29, 36, 27, 34, 25, 32, 19, 30, 18, 28, 16, 26, 15],
  [6.5, 41, 37, 39, 33, 37, 28, 35, 26, 33, 24, 31, 18, 29, 17, 27, 15, 25, 14],
  [6.0, 40, 36, 38, 32, 36, 27, 34, 25, 32, 23, 30, 17, 28, 16, 26, 14, 24, 13],
  [5.5, 39, 35, 37, 31, 35, 26, 33, 24, 31, 22, 29, 16, 27, 15, 25, 13, 23, 12],
  [5.0, 38, 34, 36, 30, 34, 25, 32, 23, 30, 21, 28, 15, 26, 14, 24, 12, 22, 11],
  [4.5, 37, 33, 35, 29, 33, 24, 31, 22, 29, 20, 27, 14, 25, 13, 23, 11, 21, 10],
  [4.0, 36, 32, 34, 28, 32, 23, 30, 21, 28, 19, 26, 13, 24, 12, 22, 10, 20, 9],
  [3.5, 35, 31, 33, 27, 31, 22, 29, 20, 27, 18, 25, 12, 23, 11, 21, 9, 19, 8],
  [3.0, 34, 30, 32, 26, 30, 21, 28, 19, 26, 17, 24, 11, 22, 10, 20, 8, 18, 7],
  [2.5, 33, 29, 31, 25, 29, 20, 27, 18, 25, 16, 23, 10, 21, 9, 19, 7, 17, 6],
];

const CLR_CRUNCH_TBL = [
  [15.0, 60, 58, 58, 56, 56, 54, 54, 52, 52, 50, 50, 48, 48, 46, 46, 44, 44, 42],
  [14.5, 59, 57, 57, 55, 55, 53, 53, 51, 51, 49, 49, 47, 47, 45, 45, 43, 43, 41],
  [14.0, 58, 56, 56, 54, 54, 52, 52, 50, 50, 48, 48, 46, 46, 44, 44, 42, 42, 40],
  [13.5, 57, 55, 55, 53, 53, 51, 51, 49, 49, 47, 47, 45, 45, 43, 43, 41, 41, 39],
  [13.0, 56, 54, 54, 52, 52, 50, 50, 48, 48, 46, 46, 44, 44, 42, 42, 40, 40, 38],
  [12.5, 55, 53, 53, 51, 51, 49, 49, 47, 47, 45, 45, 43, 43, 41, 41, 39, 39, 37],
  [12.0, 54, 52, 52, 50, 50, 48, 48, 46, 46, 44, 44, 42, 42, 40, 40, 38, 38, 36],
  [11.5, 53, 51, 51, 49, 49, 47, 47, 45, 45, 43, 43, 41, 41, 39, 39, 37, 37, 35],
  [11.0, 52, 50, 50, 48, 48, 46, 46, 44, 44, 42, 42, 40, 40, 38, 38, 36, 36, 34],
  [10.5, 51, 49, 49, 47, 47, 45, 45, 43, 43, 41, 41, 39, 39, 37, 37, 35, 35, 33],
  [10.0, 50, 48, 48, 46, 46, 44, 44, 42, 42, 40, 40, 38, 38, 36, 36, 34, 34, 32],
  [9.5, 49, 47, 47, 45, 45, 43, 43, 41, 41, 39, 39, 37, 37, 35, 35, 33, 33, 31],
  [9.0, 48, 46, 46, 44, 44, 42, 42, 40, 40, 38, 38, 36, 36, 34, 34, 32, 32, 30],
  [8.5, 47, 45, 45, 43, 43, 41, 41, 39, 39, 37, 37, 35, 35, 33, 33, 31, 31, 29],
  [8.0, 46, 44, 44, 42, 42, 40, 40, 38, 38, 36, 36, 34, 34, 32, 32, 30, 30, 28],
  [7.5, 45, 43, 43, 41, 41, 39, 39, 37, 37, 35, 35, 33, 33, 31, 31, 29, 29, 27],
  [7.0, 44, 42, 42, 40, 40, 38, 38, 36, 36, 34, 34, 32, 32, 30, 30, 28, 28, 26],
  [6.5, 43, 41, 41, 39, 39, 37, 37, 35, 35, 33, 33, 31, 31, 29, 29, 27, 27, 25],
  [6.0, 42, 40, 40, 38, 38, 36, 36, 34, 34, 32, 32, 30, 30, 28, 28, 26, 26, 24],
  [5.5, 41, 39, 39, 37, 37, 35, 35, 33, 33, 31, 31, 29, 29, 27, 27, 25, 25, 23],
  [5.0, 40, 38, 38, 36, 36, 34, 34, 32, 32, 30, 30, 28, 28, 26, 26, 24, 24, 22],
  [4.5, 39, 37, 37, 35, 35, 33, 33, 31, 31, 29, 29, 27, 27, 25, 25, 23, 23, 21],
  [4.0, 38, 36, 36, 34, 34, 32, 32, 30, 30, 28, 28, 26, 26, 24, 24, 22, 22, 20],
  [3.5, 37, 35, 35, 33, 33, 31, 31, 29, 29, 27, 27, 25, 25, 23, 23, 21, 21, 19],
  [3.0, 36, 34, 34, 32, 32, 30, 30, 28, 28, 26, 26, 24, 24, 22, 22, 20, 20, 18],
  [2.5, 35, 33, 33, 31, 31, 29, 29, 27, 27, 25, 25, 23, 23, 21, 21, 19, 19, 17],
];

// PLANK time stored as seconds, table values converted to seconds
const PLANK_TBL = [
  [15.0, 220, 215, 215, 210, 210, 205, 205, 200, 200, 195, 195, 190, 190, 185, 185, 180, 180, 175],
  [14.5, 215, 210, 210, 205, 205, 200, 200, 195, 195, 190, 190, 185, 185, 180, 180, 175, 175, 170],
  [14.0, 210, 205, 205, 200, 200, 195, 195, 190, 190, 185, 185, 180, 180, 175, 175, 170, 170, 165],
  [13.5, 205, 200, 200, 195, 195, 190, 190, 185, 185, 180, 180, 175, 175, 170, 170, 165, 165, 160],
  [13.0, 200, 195, 195, 190, 190, 185, 185, 180, 180, 175, 175, 170, 170, 165, 165, 160, 160, 155],
  [12.5, 195, 190, 190, 185, 185, 180, 180, 175, 175, 170, 170, 165, 165, 160, 160, 155, 155, 150],
  [12.0, 190, 185, 185, 180, 180, 175, 175, 170, 170, 165, 165, 160, 160, 155, 155, 150, 150, 145],
  [11.5, 185, 180, 180, 175, 175, 170, 170, 165, 165, 160, 160, 155, 155, 150, 150, 145, 145, 140],
  [11.0, 180, 175, 175, 170, 170, 165, 165, 160, 160, 155, 155, 150, 150, 145, 145, 140, 140, 135],
  [10.5, 175, 170, 170, 165, 165, 160, 160, 155, 155, 150, 150, 145, 145, 140, 140, 135, 135, 130],
  [10.0, 170, 165, 165, 160, 160, 155, 155, 150, 150, 145, 145, 140, 140, 135, 135, 130, 130, 125],
  [9.5, 165, 160, 160, 155, 155, 150, 150, 145, 145, 140, 140, 135, 135, 130, 130, 125, 125, 120],
  [9.0, 160, 155, 155, 150, 150, 145, 145, 140, 140, 135, 135, 130, 130, 125, 125, 120, 120, 115],
  [8.5, 155, 150, 150, 145, 145, 140, 140, 135, 135, 130, 130, 125, 125, 120, 120, 115, 115, 110],
  [8.0, 150, 145, 145, 140, 140, 135, 135, 130, 130, 125, 125, 120, 120, 115, 115, 110, 110, 105],
  [7.5, 145, 140, 140, 135, 135, 130, 130, 125, 125, 120, 120, 115, 115, 110, 110, 105, 105, 100],
  [7.0, 140, 135, 135, 130, 130, 125, 125, 120, 120, 115, 115, 110, 110, 105, 105, 100, 100, 95],
  [6.5, 135, 130, 130, 125, 125, 120, 120, 115, 115, 110, 110, 105, 105, 100, 100, 95, 95, 90],
  [6.0, 130, 125, 125, 120, 120, 115, 115, 110, 110, 105, 105, 100, 100, 95, 95, 90, 90, 85],
  [5.5, 125, 120, 120, 115, 115, 110, 110, 105, 105, 100, 100, 95, 95, 90, 90, 85, 85, 80],
  [5.0, 120, 115, 115, 110, 110, 105, 105, 100, 100, 95, 95, 90, 90, 85, 85, 80, 80, 75],
  [4.5, 115, 110, 110, 105, 105, 100, 100, 95, 95, 90, 90, 85, 85, 80, 80, 75, 75, 70],
  [4.0, 110, 105, 105, 100, 100, 95, 95, 90, 90, 85, 85, 80, 80, 75, 75, 70, 70, 65],
  [3.5, 105, 100, 100, 95, 95, 90, 90, 85, 85, 80, 80, 75, 75, 70, 70, 65, 65, 60],
  [3.0, 100, 95, 95, 90, 90, 85, 85, 80, 80, 75, 75, 70, 70, 65, 65, 60, 60, 55],
  [2.5, 95, 90, 90, 85, 85, 80, 80, 75, 75, 70, 70, 65, 65, 60, 60, 55, 55, 50],
];

// 2-MILE RUN: lower is better. Times in seconds. 50 pt max.
const RUN_TBL = [
  [50.0, 805, 930, 815, 955, 822, 970, 836, 972, 845, 1005, 870, 1015, 909, 1030, 928, 1063, 1018, 1100],
  [49.5, 824, 960, 834, 984, 843, 1000, 858, 1003, 869, 1035, 894, 1046, 932, 1063, 952, 1096, 1039, 1134],
  [49.0, 843, 989, 853, 1014, 864, 1031, 880, 1034, 893, 1066, 918, 1077, 955, 1096, 977, 1129, 1060, 1168],
  [48.0, 862, 1019, 872, 1043, 885, 1061, 902, 1065, 917, 1096, 942, 1108, 978, 1128, 1001, 1162, 1081, 1202],
  [47.0, 881, 1049, 891, 1072, 906, 1091, 924, 1096, 941, 1126, 965, 1139, 1001, 1161, 1026, 1194, 1102, 1236],
  [46.0, 900, 1078, 910, 1101, 928, 1121, 946, 1127, 965, 1157, 989, 1170, 1024, 1194, 1050, 1227, 1124, 1270],
  [45.0, 919, 1108, 929, 1131, 949, 1152, 968, 1157, 989, 1187, 1013, 1201, 1047, 1227, 1074, 1260, 1145, 1304],
  [44.0, 938, 1138, 948, 1160, 970, 1182, 990, 1188, 1013, 1217, 1037, 1232, 1070, 1259, 1099, 1293, 1166, 1338],
  [43.0, 957, 1167, 967, 1189, 991, 1212, 1012, 1219, 1037, 1248, 1061, 1263, 1093, 1292, 1123, 1326, 1187, 1372],
  [42.0, 976, 1197, 986, 1218, 1012, 1242, 1034, 1250, 1061, 1278, 1085, 1294, 1116, 1325, 1148, 1359, 1208, 1406],
  [41.0, 995, 1227, 1005, 1248, 1033, 1273, 1056, 1281, 1085, 1309, 1109, 1325, 1140, 1358, 1172, 1392, 1229, 1440],
  [40.0, 1014, 1256, 1024, 1277, 1054, 1303, 1078, 1312, 1108, 1339, 1132, 1356, 1163, 1390, 1196, 1424, 1250, 1474],
  [39.0, 1033, 1286, 1043, 1306, 1075, 1333, 1100, 1343, 1132, 1369, 1156, 1387, 1186, 1423, 1221, 1457, 1271, 1508],
  [38.5, 1052, 1315, 1062, 1335, 1096, 1363, 1122, 1374, 1156, 1400, 1180, 1418, 1209, 1456, 1245, 1490, 1292, 1542],
  [38.0, 1071, 1345, 1081, 1365, 1117, 1394, 1144, 1405, 1180, 1430, 1204, 1449, 1232, 1489, 1270, 1523, 1313, 1576],
  [37.5, 1090, 1375, 1100, 1394, 1139, 1424, 1166, 1436, 1204, 1460, 1228, 1480, 1255, 1521, 1294, 1556, 1335, 1610],
  [37.0, 1109, 1404, 1119, 1423, 1160, 1454, 1188, 1466, 1228, 1491, 1252, 1511, 1278, 1554, 1318, 1589, 1347, 1644],
  [36.5, 1128, 1434, 1138, 1452, 1181, 1484, 1210, 1497, 1252, 1521, 1275, 1542, 1301, 1587, 1343, 1621, 1356, 1678],
  [36.0, 1147, 1464, 1157, 1482, 1202, 1515, 1232, 1528, 1276, 1551, 1299, 1573, 1324, 1620, 1367, 1654, 1398, 1712],
  [35.5, 1176, 1493, 1176, 1511, 1223, 1545, 1254, 1559, 1300, 1582, 1323, 1604, 1347, 1652, 1392, 1687, 1419, 1746],
  [35.0, 1185, 1523, 1195, 1540, 1244, 1575, 1276, 1590, 1324, 1612, 1347, 1635, 1370, 1685, 1416, 1720, 1440, 1780],
];

// 20m HAMR: shuttles. Higher is better. 50 pt max.
const HAMR_TBL = [
  [50.0, 87, 68, 85, 65, 84, 63, 82, 63, 81, 59, 77, 58, 71, 57, 69, 53, 65, 50],
  [49.5, 84, 65, 82, 62, 81, 60, 79, 60, 77, 56, 73, 55, 68, 53, 66, 50, 62, 47],
  [49.0, 81, 61, 79, 58, 78, 57, 75, 56, 73, 53, 70, 52, 65, 50, 63, 47, 59, 44],
  [48.0, 78, 58, 76, 55, 75, 53, 72, 53, 70, 50, 67, 49, 62, 47, 60, 44, 56, 41],
  [47.0, 75, 55, 74, 52, 72, 51, 69, 50, 67, 47, 64, 46, 60, 44, 57, 42, 54, 38],
  [46.0, 72, 52, 71, 50, 69, 48, 66, 47, 64, 45, 61, 44, 57, 42, 55, 39, 52, 36],
  [45.0, 70, 49, 69, 47, 66, 45, 64, 45, 61, 42, 58, 41, 55, 39, 52, 37, 49, 34],
  [44.0, 67, 46, 66, 44, 63, 43, 61, 42, 58, 40, 56, 39, 53, 37, 50, 34, 47, 32],
  [43.0, 65, 44, 64, 42, 61, 40, 59, 40, 56, 38, 53, 37, 50, 35, 48, 32, 45, 29],
  [42.0, 63, 41, 62, 40, 59, 38, 56, 37, 53, 35, 51, 34, 48, 32, 45, 30, 43, 27],
  [41.0, 60, 39, 59, 38, 56, 36, 54, 35, 51, 33, 49, 32, 46, 30, 43, 28, 41, 26],
  [40.0, 58, 37, 57, 36, 54, 34, 52, 33, 49, 31, 47, 30, 44, 28, 41, 26, 39, 24],
  [39.0, 56, 35, 55, 34, 52, 32, 50, 31, 47, 30, 45, 29, 42, 26, 40, 25, 38, 22],
  [38.5, 54, 33, 53, 32, 50, 30, 48, 29, 45, 28, 43, 27, 40, 25, 38, 23, 36, 20],
  [38.0, 52, 31, 52, 30, 48, 28, 46, 28, 43, 26, 41, 25, 39, 23, 36, 21, 34, 19],
  [37.5, 51, 29, 50, 28, 46, 26, 44, 26, 41, 24, 39, 23, 37, 21, 34, 20, 33, 18],
  [37.0, 49, 28, 48, 26, 44, 25, 42, 24, 39, 23, 37, 22, 35, 20, 33, 18, 31, 17],
  [36.5, 47, 26, 46, 25, 43, 23, 40, 23, 37, 21, 36, 20, 34, 18, 31, 17, 30, 14],
  [36.0, 46, 24, 45, 23, 41, 22, 39, 21, 36, 20, 34, 19, 32, 17, 30, 15, 28, 13],
  [35.5, 44, 23, 43, 22, 39, 20, 37, 20, 34, 19, 32, 18, 31, 16, 28, 14, 27, 12],
  [35.0, 42, 21, 42, 20, 38, 19, 36, 18, 32, 17, 31, 16, 30, 14, 27, 13, 26, 11],
];

// 2km Walk: max time per age range. Pass/fail only, no scoring beyond pass.
const WALK_MAX_M = [976, 978, 978, 978, 983, 1000, 1000, 1000, 1018]; // seconds, by age band
const WALK_MAX_F = [1042, 1048, 1048, 1048, 1069, 1091, 1091, 1091, 1133];

const scorePushup = (reps, age, isFemale) => lookupHigher(PUSHUP_TBL, reps, ageBandIdx(age), isFemale);
const scoreHRPushup = (reps, age, isFemale) => lookupHigher(HR_PUSHUP_TBL, reps, ageBandIdx(age), isFemale);
const scoreSitup = (reps, age, isFemale) => lookupHigher(SITUP_TBL, reps, ageBandIdx(age), isFemale);
const scoreCLR = (reps, age, isFemale) => lookupHigher(CLR_CRUNCH_TBL, reps, ageBandIdx(age), isFemale);
const scorePlank = (timeMmss, age, isFemale) => {
  const sec = mmssToSec(timeMmss);
  return sec === null ? null : lookupHigher(PLANK_TBL, sec, ageBandIdx(age), isFemale);
};
const scoreRun = (timeMmss, age, isFemale) => {
  if (typeof timeMmss === "string" && timeMmss.trim().toUpperCase() === "DNF") return 0;
  const sec = mmssToSec(timeMmss);
  return sec === null ? null : lookupLower(RUN_TBL, sec, ageBandIdx(age), isFemale);
};
const scoreHAMR = (shuttles, age, isFemale) => lookupHigher(HAMR_TBL, shuttles, ageBandIdx(age), isFemale);
const scoreWalk = (timeMmss, age, isFemale) => {
  const sec = mmssToSec(timeMmss);
  if (sec === null) return null;
  const max = (isFemale ? WALK_MAX_F : WALK_MAX_M)[ageBandIdx(age)];
  return sec <= max ? "Pass" : "Fail";
};

// Score a full test entry. Returns { bca, strength, core, cardio, total, components }
const scoreTest = (test, age, isFemale) => {
  if (!test || !age || isFemale === undefined || isFemale === null) return null;
  const bca = scoreBCA(test.bcaWHtR);
  let strength = null;
  if (test.strengthType === "pushup") strength = scorePushup(test.strengthValue, age, isFemale);
  else if (test.strengthType === "hr_pushup") strength = scoreHRPushup(test.strengthValue, age, isFemale);
  let core = null;
  if (test.coreType === "situp") core = scoreSitup(test.coreValue, age, isFemale);
  else if (test.coreType === "clr_crunch") core = scoreCLR(test.coreValue, age, isFemale);
  else if (test.coreType === "plank") core = scorePlank(test.coreValue, age, isFemale);
  let cardio = null;
  if (test.cardioType === "run") cardio = scoreRun(test.cardioValue, age, isFemale);
  else if (test.cardioType === "hamr") cardio = scoreHAMR(test.cardioValue, age, isFemale);
  else if (test.cardioType === "walk") cardio = scoreWalk(test.cardioValue, age, isFemale);

  const numericCardio = typeof cardio === "number" ? cardio : 0;
  const components = [bca, strength, core, numericCardio];
  const allPresent = components.every((v) => v !== null && v !== undefined);
  const total = allPresent ? components.reduce((a, b) => a + b, 0) : null;
  return { bca, strength, core, cardio, total };
};

const scoreMockAssessment = (test) => {
  const age = AGE_BAND_REPRESENTATIVE[test?.ageBand];
  if (!age || !["M", "F"].includes(test?.sexForScoring)) return null;
  const scored = scoreTest({
    bcaWHtR: test.bcaWhtr,
    strengthType: test.strengthType,
    strengthValue: test.strengthValue,
    coreType: test.coreType,
    coreValue: test.coreValue,
    cardioType: test.cardioType,
    cardioValue: test.cardioValue
  }, age, test.sexForScoring === "F");
  if (!scored) return null;
  const componentPass = {
    bca: typeof scored.bca === "number" && scored.bca >= 17,
    strength: typeof scored.strength === "number" && scored.strength >= 6,
    core: typeof scored.core === "number" && scored.core >= 6,
    cardio: typeof scored.cardio === "number" && scored.cardio >= 31
  };
  return {
    ...scored,
    componentPass,
    passed: scored.total !== null &&
      scored.total >= 75 &&
      Object.values(componentPass).every(Boolean),
    scoringRuleVersion: SCORING_RULE_VERSION
  };
};

const SCORING_RULES = {
  version: SCORING_RULE_VERSION,
  ageBands: AGE_BANDS,
  ageBandRepresentative: AGE_BAND_REPRESENTATIVE,
  bca: BCA_TBL,
  pushup: PUSHUP_TBL,
  hrPushup: HR_PUSHUP_TBL,
  situp: SITUP_TBL,
  clrCrunch: CLR_CRUNCH_TBL,
  plank: PLANK_TBL,
  run: RUN_TBL,
  hamr: HAMR_TBL
};

export {
  AGE_BANDS,
  SCORING_RULES,
  SCORING_RULE_VERSION,
  scoreMockAssessment,
  scoreTest
};
